import {
  ArchiveBoxArrowDownIcon,
  ArchiveBoxIcon,
  FlagIcon,
  MagnifyingGlassIcon,
  MinusCircleIcon,
  PencilSquareIcon,
  TrashIcon,
  UserGroupIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import api from "@src/api/api";
import ContextMenu from "@src/components/elements/shared/ContextMenu";
import { showReportModal } from "@src/state/reportModal/actions";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";

type Props = {
  archived?: boolean;
  isAdmin?: boolean;
  chatId: string;
  userId: string;
  exitChat: () => void;
  toggleSearch?: () => void;
  openEditModal?: () => void;
  profileLink?: () => void;
  isGroup?: boolean;
};
const ChatDropdown = ({
  archived,
  chatId,
  exitChat,
  toggleSearch,
  userId,
  openEditModal,
  isGroup,
  isAdmin,
  profileLink,
}: Props) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const archiveChat = useMutation(["chat-archive"], {
    mutationFn: async (chatId: string) => {
      const res = await api.get(`/api/v1/chats/${chatId}/archive`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["chat-" + chatId]);
      queryClient.invalidateQueries(["chats"]);
    },
    onError: () => {
      alert("Failed");
    },
  });

  const unArchiveChat = useMutation(["chat-un-archive"], {
    mutationFn: async (chatId: string) => {
      const res = await api.delete(`/api/v1/chats/${chatId}/archive`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["chat-" + chatId]);
      queryClient.invalidateQueries(["chats"]);
    },
    onError: () => {
      alert("Failed");
    },
  });

  const deleteChat = useMutation(["chat-delete"], {
    mutationFn: async () => {
      const res = await api.delete(`/api/v1/chats/${chatId}`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["chat-" + chatId]);
      queryClient.invalidateQueries(["chats"]);
    },
    onError: () => {
      alert("Failed");
    },
  });

  const block = useMutation([`block-${userId}`], {
    mutationFn: async () => {
      const res = await api.get(`/api/v1/users/${userId}/block`);
      return res.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["chat-" + chatId]);
      queryClient.invalidateQueries(["chats"]);
    },
    onError: () => {
      alert("Failed");
    },
  });
  const { t } = useTranslation();

  return (
    <>
      <ContextMenu
        items={[
          toggleSearch && {
            label: t("main.feedTabs.placeholder"),
            onClick: () => {
              toggleSearch();
            },
            icon: MagnifyingGlassIcon,
          },
          openEditModal &&
            isGroup &&
            isAdmin && {
              label: t("main.profileViewTabs.aboutMe.content.sidebarButtons.edit"),
              onClick: () => {
                openEditModal();
              },
              icon: PencilSquareIcon,
            },
          !archived
            ? {
                label: t("pages.messenger.archive"),
                onClick: () => {
                  archiveChat.mutate(chatId);
                },
                icon: ArchiveBoxArrowDownIcon,
              }
            : {
                label: t("pages.messenger.unarchive"),
                onClick: () => {
                  unArchiveChat.mutate(chatId);
                },
                icon: ArchiveBoxIcon,
              },
          {
            label: t("buttons.report"),
            onClick: () => dispatch(showReportModal(chatId, "chat")),
            icon: FlagIcon,
          },
          {
            label: isGroup ? t("pages.messenger.blockGroup") : t("pages.messenger.blockUser"),
            onClick: () => {
              exitChat();
              block.mutate();
            },
            icon: MinusCircleIcon,
          },
          {
            label: isGroup ? t("pages.messenger.groupProfile") : t("pages.messenger.userProfile"),
            onClick: () => {
              profileLink && profileLink();
            },
            icon: isGroup ? UserGroupIcon : UserIcon,
          },
          {
            label: t("pages.messenger.deleteChat"),
            onClick: () => {
              exitChat();
              deleteChat.mutate();
            },
            icon: TrashIcon,
          },
        ]}
      />
    </>
  );
};

export default ChatDropdown;
